import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Lock, ShieldAlert, Pin, Trash2, SmilePlus, CheckCheck, Forward, Pencil, Languages, Loader2, MoreHorizontal, Coins, ExternalLink, Play, Pause, Eye, EyeOff, Reply, Camera } from "lucide-react";
import { PollView, ChecklistView, ContactView, FileView } from "@/components/chat/RichMessages";
import { LinkPreview } from "@/components/chat/LinkPreview";
import { YouTubeEmbed, extractYouTubeIds } from "@/components/chat/YouTubeEmbed";
import { useBubbleColor, bubbleClasses } from "@/lib/bubbleColor";
import { api, client } from "@/lib/api";
import { Avatar } from "@/components/common/Avatar";
import { VerifiedCheck } from "@/components/common/TrustBadge";
import { clockTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { AuthImage } from "@/components/common/AuthImage";
import { scanText } from "@/lib/shield";
import { ReactionChip } from "@/components/chat/ReactionChip";

export const QUICK_EMOJIS = ["🔥", "👍", "❤️", "😂", "🎉", "🙏"];

export function imgSrc(img) {
  return img.startsWith("data:") ? img : `data:image/jpeg;base64,${img}`;
}

// Voice note — fetches the Bearer audio blob on first play and renders a waveform + duration.
function VoicePlayer({ chatId, msg, isOwn }) {
  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const wave = Array.isArray(msg.voice_wave) && msg.voice_wave.length ? msg.voice_wave : Array.from({ length: 24 }, () => 35);
  const dur = Math.round(msg.voice_dur || 0);

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); return; }
    if (!a.src) {
      setLoading(true);
      try {
        const res = await client.get(api.messageVoiceSrc(chatId, msg.id), { responseType: "blob" });
        urlRef.current = URL.createObjectURL(res.data);
        a.src = urlRef.current;
      } catch {
        toast.error("Couldn't load this voice message.");
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    a.play();
  };

  return (
    <div className="flex items-center gap-2.5 py-0.5" data-testid={`msg-voice-${msg.id}`}>
      <button type="button" onClick={toggle} className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-full", isOwn ? "bg-bloq-bg/25 text-bloq-bg" : "bg-bloq-mint/15 text-bloq-mint")} data-testid={`voice-play-${msg.id}`}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </button>
      <div className="flex h-6 items-end gap-[2px]">
        {wave.slice(0, 32).map((h, i) => (
          <span key={i} className={cn("w-[3px] rounded-full", isOwn ? "bg-bloq-bg/50" : "bg-bloq-mint/60")} style={{ height: `${Math.max(12, Math.min(100, h))}%` }} />
        ))}
      </div>
      <span className="text-[11px] opacity-80">{Math.floor(dur / 60)}:{String(dur % 60).padStart(2, "0")}</span>
      <audio ref={audioRef} className="hidden" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />
    </div>
  );
}

// View-once media — recipient taps to reveal; opening marks it consumed server-side.
function ViewOnceView({ chatId, msg, isOwn }) {
  const opened = !!msg.view_once?.opened;
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);

  const open = async () => {
    setBusy(true);
    try { await api.openViewOnce(chatId, msg.id); setRevealed(true); }
    catch { toast.error("Couldn't open — it may have expired."); }
    finally { setBusy(false); }
  };

  if (isOwn) {
    return <div className="flex items-center gap-1.5 text-[13px] opacity-90" data-testid={`viewonce-sent-${msg.id}`}><Eye className="h-3.5 w-3.5" /> View once · {opened ? "opened" : "sent"}</div>;
  }
  if (revealed) {
    return <AuthImage src={api.messageImageSrc(chatId, msg.id)} alt="View-once media" className="block max-h-72 w-auto max-w-full rounded-xl object-cover" testid={`viewonce-media-${msg.id}`} />;
  }
  if (opened) {
    return <div className="flex items-center gap-1.5 text-[13px] italic text-bloq-muted" data-testid={`viewonce-opened-${msg.id}`}><EyeOff className="h-3.5 w-3.5" /> View once · opened</div>;
  }
  return (
    <button type="button" onClick={open} disabled={busy} className="flex items-center gap-2 rounded-xl border border-bloq-mint/30 bg-bloq-mint/10 px-3 py-2 text-sm font-semibold text-bloq-mint disabled:opacity-60" data-testid={`viewonce-open-${msg.id}`}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} View once — tap to view
    </button>
  );
}

export function MessageBubble({ msg, isOwn, isCircle, members, currentUserId, otherId, me, peer, official, onReact, onDelete, onForward, onReply, onPin, onEdit, onVote, onToggle, onExplain, pickerOpen, setPicker, payload, onImageClick, chatId, autoTranslation, onOpenLink }) {
  const sender = msg.sender || members?.[msg.sender_id];
  const reactions = Object.entries(msg.reactions || {}).filter(([, u]) => (u || []).length > 0);
  // Reactor ids → roster entries (group members map/list, else the DM pair) for the "who reacted" popover.
  const resolveUser = (id) => {
    const m = members?.[id] || (Array.isArray(members) ? members.find((u) => u?.id === id || u?.user_id === id) : null);
    if (m) return { ...m, isMe: id === currentUserId };
    if (id === currentUserId) return { ...(me || {}), display_name: me?.display_name || "You", isMe: true };
    if (id === otherId && peer) return peer;
    return { display_name: "Member" };
  };
  const seenByOther = isOwn && otherId && (msg.read_by || []).includes(otherId);
  const encrypted = !!msg.e2ee;
  const pending = encrypted && payload === undefined;
  const noKey = encrypted && payload === null;
  const imageSrc = payload?.image ? imgSrc(payload.image) : null;
  const bodyText = payload?.text ?? "";
  const isRich = msg.type === "poll" || msg.type === "checklist" || msg.type === "contact" || msg.type === "file" || msg.type === "video" || msg.type === "gif";
  const isSticker = msg.type === "sticker" && !!msg.sticker;
  const isCrypto = !!msg.crypto;
  const canForward = !!onForward && !encrypted && !isRich && !isCrypto && !msg.deleted;
  const canReply = !!onReply && !msg.deleted && !isCrypto;
  const canPin = !!onPin && !msg.deleted;
  const canEdit = !!onEdit && isOwn && msg.type === "text" && !encrypted && !msg.deleted;
  const ownBubble = bubbleClasses(useBubbleColor());
  // Shield Everywhere (cc_24): sealed text never reaches the server scanner, so verdicts for Cipher Lock
  // messages are computed on-device from GET /shield/rules (same heuristics → same verdicts).
  const [localScan, setLocalScan] = useState(null);
  useEffect(() => {
    if (!encrypted || !bodyText) { setLocalScan(null); return undefined; }
    let cancelled = false;
    scanText(bodyText).then((r) => { if (!cancelled) setLocalScan(r); });
    return () => { cancelled = true; };
  }, [encrypted, bodyText]);
  const links = msg.links?.length ? msg.links : localScan?.links || [];
  const scamIntent = !!msg.scam_intent || !!localScan?.scam_intent;
  const ytIds = extractYouTubeIds(bodyText, links);

  // AI superpowers (Lane C): per-message translate + Shield "why is this risky?".
  const [translation, setTranslation] = useState(null); // { translated, source_lang }
  const [translating, setTranslating] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false); // mobile: tap ⋯ to reveal the action toolbar
  // Babel (cc_24): an auto-translation from the thread seeds the same toggle UI as a manual translate.
  useEffect(() => {
    if (autoTranslation?.translated) { setTranslation(autoTranslation); setShowOriginal(false); }
  }, [autoTranslation]);
  const canTranslate = !!bodyText && !isRich && !isSticker && !isCrypto && !msg.deleted;
  const canExplain = !!onExplain && !isOwn && !!bodyText && !isRich && !isSticker && !isCrypto && !msg.deleted;

  const doTranslate = async () => {
    if (translation) {
      setShowOriginal((s) => !s);
      return;
    }
    setTranslating(true);
    try {
      const lang = (navigator.language || "en").slice(0, 2);
      // Sealed (Cipher Lock) text never leaves as a message id — translate the decrypted text ephemerally.
      const res = encrypted ? await api.translateText(bodyText, true) : await api.translateMessage(msg.id, lang);
      if (!res.translated) { toast("Already in your language"); return; }
      setTranslation({ translated: res.translated, source_lang: res.source_lang });
      setShowOriginal(false);
    } catch (e) {
      if (e?.response?.status !== 429) toast.error("Couldn't translate this message.");
    } finally {
      setTranslating(false);
    }
  };
  const displayText = translation && !showOriginal ? translation.translated : bodyText;

  return (
    <div className={cn("group flex w-full gap-2.5", isOwn ? "justify-end" : "justify-start")}>
      {!isOwn && isCircle && sender && <Avatar user={sender} size="xs" className="mt-0.5 self-start" />}
      <div className="relative min-w-0 max-w-[78%] sm:max-w-[64%]">
        {!isOwn && isCircle && sender && (
          <div className="mb-1 ml-1 flex items-center gap-1 text-xs font-semibold text-bloq-blue">
            {sender.display_name}
            {sender.verified && <VerifiedCheck size={12} />}
          </div>
        )}

        <div className={cn("relative flex min-w-0 items-center gap-1.5", isOwn && "flex-row-reverse")}>
          {msg.deleted ? (
            <div className="rounded-2xl border border-white/8 bg-bloq-s2/50 px-4 py-2.5 text-[14px] italic text-bloq-muted" data-testid="message-deleted">
              This message was deleted
            </div>
          ) : (
            <div
              className={cn(
                "min-w-0 rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed",
                isSticker
                  ? "bg-transparent p-0"
                  : isRich
                    ? "rounded-bl-md border border-white/8 bg-bloq-s2 text-white"
                    : isOwn
                      ? cn("rounded-br-md bg-gradient-to-br", ownBubble)
                      : "rounded-bl-md border border-white/8 bg-bloq-s2 text-white",
              )}
            >
              {msg.story_ref && (
                <div
                  className={cn(
                    "mb-1.5 flex items-center gap-1.5 rounded-lg border-l-2 px-2 py-1 text-[12px] font-semibold",
                    isOwn ? "border-bloq-bg/40 bg-bloq-bg/10 text-bloq-bg/85" : "border-bloq-purple/60 bg-bloq-purple/12 text-bloq-purple",
                  )}
                  data-testid={`msg-story-ref-${msg.id}`}
                >
                  <Camera className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{isOwn ? "Replied to their story" : "Replied to your story"}</span>
                </div>
              )}
              {msg.reply_to && (
                <div
                  className={cn(
                    "mb-1.5 rounded-lg border-l-2 px-2 py-1 text-[12px]",
                    isOwn ? "border-bloq-bg/40 bg-bloq-bg/10" : "border-bloq-mint/50 bg-white/5",
                  )}
                  data-testid={`msg-reply-quote-${msg.id}`}
                >
                  <div className={cn("truncate font-semibold", isOwn ? "text-bloq-bg/90" : "text-bloq-mint")}>
                    {msg.reply_to.sender_name || "Message"}
                  </div>
                  <div className={cn("truncate", isOwn ? "text-bloq-bg/70" : "text-bloq-muted")}>
                    {msg.reply_to.deleted
                      ? "Deleted message"
                      : msg.reply_to.image
                        ? "📷 Photo"
                        : msg.reply_to.gif
                          ? "🎞 GIF"
                        : msg.reply_to.voice
                          ? "🎤 Voice note"
                          : msg.reply_to.preview || (msg.reply_to.e2ee ? "Encrypted message" : "Message")}
                  </div>
                </div>
              )}
              {scamIntent && !official && (
                <div className="mb-1.5 flex items-center gap-1.5 rounded-lg bg-destructive/20 px-2 py-1 text-[11px] font-semibold text-destructive" data-testid={`msg-scam-intent-${msg.id}`}>
                  <ShieldAlert className="h-3.5 w-3.5" /> Shield: possible scam
                </div>
              )}
              {pending ? (
                <p className="flex items-center gap-1.5 text-[14px] italic opacity-80" data-testid="msg-decrypting">
                  <Lock className="h-3.5 w-3.5" /> Decrypting…
                </p>
              ) : noKey ? (
                <p className="flex items-center gap-1.5 text-[14px] italic opacity-80" data-testid="msg-no-key">
                  <Lock className="h-3.5 w-3.5" /> Encrypted — key not on this device yet
                </p>
              ) : isCrypto ? (
                <div className="flex items-center gap-2.5" data-testid={`msg-crypto-${msg.id}`}>
                  <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-full", isOwn ? "bg-bloq-bg/20" : "bg-bloq-mint/15 text-bloq-mint")}>
                    <Coins className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold">{isOwn ? "Sent" : "Received"} {msg.crypto.amount} {msg.crypto.symbol || ""}</div>
                    <div className="flex items-center gap-1.5 text-[11px] opacity-80">
                      <span className="capitalize">{msg.crypto.status || "pending"}</span>
                      {msg.crypto.explorer_url && (
                        <a href={msg.crypto.explorer_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 underline" data-testid={`msg-crypto-explorer-${msg.id}`}>
                          View <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ) : msg.type === "poll" && msg.poll ? (
                <PollView poll={msg.poll} msgId={msg.id} currentUserId={currentUserId} onVote={onVote} />
              ) : msg.gif?.url ? (
                <img
                  src={msg.gif.url}
                  alt={msg.gif.title || "GIF"}
                  loading="lazy"
                  style={msg.gif.width && msg.gif.height ? { aspectRatio: `${msg.gif.width} / ${msg.gif.height}` } : undefined}
                  className="block max-h-64 w-auto max-w-full rounded-xl"
                  data-testid={`msg-gif-${msg.id}`}
                />
              ) : msg.type === "checklist" && msg.checklist ? (
                <ChecklistView checklist={msg.checklist} msgId={msg.id} onToggle={onToggle} />
              ) : msg.type === "contact" && msg.contact ? (
                <ContactView contact={msg.contact} msgId={msg.id} />
              ) : (msg.type === "file" || msg.type === "video") && msg.file ? (
                <FileView
                  file={msg.file}
                  msgId={msg.id}
                  onImageClick={onImageClick}
                  isOwn={isOwn}
                />
              ) : isSticker ? (
                <AuthImage src={api.stickerImageSrc(msg.sticker.id)} alt={msg.sticker.emoji || "sticker"} className="h-28 w-28 object-contain" testid={`msg-sticker-${msg.id}`} />
              ) : msg.view_once ? (
                <ViewOnceView chatId={chatId} msg={msg} isOwn={isOwn} />
              ) : msg.has_voice ? (
                <VoicePlayer chatId={chatId} msg={msg} isOwn={isOwn} />
              ) : (
                <>
                  {imageSrc && (
                    <button type="button" onClick={() => onImageClick?.(imageSrc)} className="mb-1 block max-w-full overflow-hidden rounded-xl" data-testid={`msg-image-${msg.id}`}>
                      <img src={imageSrc} alt="Shared attachment" loading="lazy" className="max-h-72 w-auto max-w-full rounded-xl object-cover" />
                    </button>
                  )}
                  {!imageSrc && msg.image_thumb && (
                    <AuthImage src={api.messageImageSrc(chatId, msg.id)} alt="Shared image" className="mb-1 block max-h-72 w-auto max-w-full rounded-xl object-cover" testid={`msg-image-server-${msg.id}`} />
                  )}
                  {bodyText && (
                    <>
                      <p className="whitespace-pre-wrap break-words">{displayText}</p>
                      {translation && (
                        <button
                          type="button"
                          onClick={() => setShowOriginal((s) => !s)}
                          className="mt-1 text-[11px] underline opacity-70 transition-opacity hover:opacity-100"
                          data-testid={`msg-translate-toggle-${msg.id}`}
                        >
                          {showOriginal
                            ? "Show translation"
                            : `Translated${translation.source_lang ? ` from ${String(translation.source_lang).toUpperCase()}` : ""} · Show original`}
                        </button>
                      )}
                    </>
                  )}
                </>
              )}
              {links.filter((l) => extractYouTubeIds("", [l]).length === 0).map((l, i) => <LinkPreview key={i} link={l} testid={`link-preview-${msg.id}-${i}`} />)}
              {ytIds.map((vid, i) => <YouTubeEmbed key={vid} videoId={vid} testid={`msg-youtube-${msg.id}-${i}`} />)}
            </div>
          )}

          {!msg.deleted && (
            <div
              className={cn(
                "absolute -top-3 z-20 flex items-center gap-0.5 rounded-full border border-white/10 bg-bloq-s1/95 p-0.5 shadow-lg backdrop-blur transition-opacity",
                isOwn ? "right-1" : "left-1",
                actionsOpen ? "opacity-100" : "pointer-events-none opacity-0",
                "md:opacity-0 md:pointer-events-none md:group-hover:pointer-events-auto md:group-hover:opacity-100 md:group-focus-within:pointer-events-auto md:group-focus-within:opacity-100",
              )}
              data-testid={`msg-actions-${msg.id}`}
            >
              <button
                onClick={() => { setPicker(pickerOpen ? null : msg.id); setActionsOpen(false); }}
                className="flex h-7 w-7 items-center justify-center rounded-full text-bloq-muted hover:bg-white/8 hover:text-white"
                data-testid={`msg-react-btn-${msg.id}`}
                title="React"
              >
                <SmilePlus className="h-4 w-4" />
              </button>
              {canTranslate && (
                <button
                  onClick={() => { setActionsOpen(false); doTranslate(); }}
                  disabled={translating}
                  className="flex h-7 w-7 items-center justify-center rounded-full text-bloq-muted hover:bg-white/8 hover:text-bloq-mint disabled:opacity-50"
                  data-testid={`msg-translate-btn-${msg.id}`}
                  title={translation ? "Toggle translation" : "Translate"}
                >
                  {translating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Languages className="h-3.5 w-3.5" />}
                </button>
              )}
              {canExplain && (
                <button
                  onClick={() => { setActionsOpen(false); onExplain(bodyText); }}
                  className="flex h-7 w-7 items-center justify-center rounded-full text-bloq-muted hover:bg-white/8 hover:text-bloq-mint"
                  data-testid={`msg-explain-btn-${msg.id}`}
                  title="Ask Shield why this may be risky"
                >
                  <ShieldAlert className="h-3.5 w-3.5" />
                </button>
              )}
              {canEdit && (
                <button
                  onClick={() => onEdit(msg)}
                  className="flex h-7 w-7 items-center justify-center rounded-full text-bloq-muted hover:bg-white/8 hover:text-bloq-mint"
                  data-testid={`msg-edit-btn-${msg.id}`}
                  title="Edit"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              {canReply && (
                <button
                  onClick={() => { setActionsOpen(false); onReply(msg); }}
                  className="flex h-7 w-7 items-center justify-center rounded-full text-bloq-muted hover:bg-white/8 hover:text-bloq-mint"
                  data-testid={`msg-reply-btn-${msg.id}`}
                  title="Reply"
                >
                  <Reply className="h-3.5 w-3.5" />
                </button>
              )}
              {canForward && (
                <button
                  onClick={() => onForward(msg.id)}
                  className="flex h-7 w-7 items-center justify-center rounded-full text-bloq-muted hover:bg-white/8 hover:text-bloq-mint"
                  data-testid={`msg-forward-btn-${msg.id}`}
                  title="Forward"
                >
                  <Forward className="h-3.5 w-3.5" />
                </button>
              )}
              {canPin && (
                <button
                  onClick={() => onPin(msg.id)}
                  className="flex h-7 w-7 items-center justify-center rounded-full text-bloq-muted hover:bg-white/8 hover:text-bloq-mint"
                  data-testid={`msg-pin-btn-${msg.id}`}
                  title="Pin to top"
                >
                  <Pin className="h-3.5 w-3.5" />
                </button>
              )}
              {isOwn && (
                <button
                  onClick={() => onDelete(msg.id)}
                  className="flex h-7 w-7 items-center justify-center rounded-full text-bloq-muted hover:bg-destructive/15 hover:text-destructive"
                  data-testid={`msg-delete-btn-${msg.id}`}
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
          {!msg.deleted && (
            <button
              type="button"
              onClick={() => setActionsOpen((o) => !o)}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-white md:hidden"
              data-testid={`msg-more-btn-${msg.id}`}
              title="Message actions"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          )}
        </div>

        {pickerOpen && (
          <div className={cn("mt-1.5 flex w-fit gap-1 rounded-full border border-white/12 bg-bloq-s1 p-1 shadow-lg", isOwn && "ml-auto")} data-testid={`emoji-picker-${msg.id}`}>
            {QUICK_EMOJIS.map((e) => (
              <button
                key={e}
                onClick={() => { onReact(msg.id, e); setPicker(null); }}
                className="flex h-8 w-8 items-center justify-center rounded-full text-lg transition-transform hover:scale-125"
                data-testid={`react-emoji-${e}`}
              >
                {e}
              </button>
            ))}
          </div>
        )}

        {reactions.length > 0 && (
          <div className={cn("mt-1 flex flex-wrap gap-1", isOwn && "justify-end")}>
            {reactions.map(([emoji, users]) => (
              <ReactionChip
                key={emoji}
                emoji={emoji}
                users={users}
                mine={users.includes(currentUserId)}
                resolveUser={resolveUser}
                onToggle={() => onReact(msg.id, emoji)}
                testid={`reaction-chip-${msg.id}-${emoji}`}
              />
            ))}
          </div>
        )}

        <div className={cn("mt-1 flex items-center gap-1 text-[10px] text-bloq-muted", isOwn ? "justify-end" : "justify-start")}>
          {clockTime(msg.created_at)}
          {msg.edited && <span data-testid={`msg-edited-${msg.id}`}>· edited</span>}
          {seenByOther && <CheckCheck className="h-3 w-3 text-bloq-mint" data-testid={`read-receipt-${msg.id}`} />}
        </div>
      </div>
    </div>
  );
}
